import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { initAnalytics, applyConsent } from "@/lib/analytics";

const CONSENT_KEY = "unveiled_consent_v1";

const DEFAULT_CONSENT = { analytics: false, advertising: false, decided: false };

const ConsentContext = createContext({
    consent: DEFAULT_CONSENT,
    setConsent: () => {},
    acceptAll: () => {},
    rejectAll: () => {},
    openPreferences: () => {},
    preferencesOpen: false,
    closePreferences: () => {},
});

function loadStoredConsent() {
    try {
        const raw = localStorage.getItem(CONSENT_KEY);
        if (!raw) return DEFAULT_CONSENT;
        const parsed = JSON.parse(raw);
        return {
            analytics: !!parsed.analytics,
            advertising: !!parsed.advertising,
            decided: !!parsed.decided,
        };
    } catch {
        return DEFAULT_CONSENT;
    }
}

function persistConsent(consent) {
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify({
            ...consent,
            updated_at: new Date().toISOString(),
        }));
    } catch {}
}

export function ConsentProvider({ children }) {
    const [consent, setConsentState] = useState(DEFAULT_CONSENT);
    const [preferencesOpen, setPreferencesOpen] = useState(false);

    // On boot: initialise analytics with consent defaults (denied by default),
    // then hydrate from localStorage if the user has decided before.
    useEffect(() => {
        const stored = loadStoredConsent();
        initAnalytics({ analytics: stored.analytics, advertising: stored.advertising });
        setConsentState(stored);
    }, []);

    const commit = useCallback((next) => {
        const merged = {
            analytics: !!next.analytics,
            advertising: !!next.advertising,
            decided: true,
        };
        persistConsent(merged);
        applyConsent({ analytics: merged.analytics, advertising: merged.advertising });
        setConsentState(merged);
    }, []);

    const setConsent = useCallback((next) => commit(next), [commit]);
    const acceptAll = useCallback(
        () => commit({ analytics: true, advertising: true }),
        [commit],
    );
    const rejectAll = useCallback(
        () => commit({ analytics: false, advertising: false }),
        [commit],
    );

    const openPreferences = useCallback(() => setPreferencesOpen(true), []);
    const closePreferences = useCallback(() => setPreferencesOpen(false), []);

    return (
        <ConsentContext.Provider
            value={{ consent, setConsent, acceptAll, rejectAll, openPreferences, preferencesOpen, closePreferences }}
        >
            {children}
        </ConsentContext.Provider>
    );
}

export function useConsent() {
    return useContext(ConsentContext);
}
